/**
 * Discount payloads carry `discount_value` as an absolute amount off — money,
 * not a percentage. The API derives `discount_percentage` and `final_price`
 * from the seller's own price, so read those instead of doing the arithmetic
 * here; dividing or suffixing `%` onto `discount_value` produced percentages
 * like -117.78% and negative prices.
 */

/** Formats a percentage for a badge: 10 -> "10%", 12.5 -> "12.5%". */
export function formatDiscountPercent(percentage) {
    if (percentage === null || percentage === undefined || Number.isNaN(Number(percentage))) {
        return null;
    }

    const value = Number(percentage);

    return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}

/**
 * Formats the absolute amount off. organization_price and initial_price are
 * stored in EUR base currency, so discount_value is EUR too.
 */
export function formatAmountOff(discountValue) {
    if (discountValue === null || discountValue === undefined || Number.isNaN(Number(discountValue))) {
        return null;
    }

    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(Number(discountValue));
}

/**
 * A badge label that prefers the percentage and falls back to the amount off
 * when the seller has no price to compute a percentage against.
 */
export function formatDiscountBadge(row) {
    return formatDiscountPercent(row.discount_percentage) ?? formatAmountOff(row.discount_value);
}

/**
 * Discounted amounts in both EUR and the seller's local currency. The local
 * amount is scaled by the same ratio as the EUR one, since only EUR carries a
 * server-computed final price.
 */
export function resolveDiscountedAmounts(row) {
    const initial = Number(row.initial_price) || null;
    const final = row.final_price !== null && row.final_price !== undefined
        ? Number(row.final_price)
        : null;

    if (initial === null || final === null) {
        return { eurAmount: null, localAmount: null };
    }

    const localInitial = Number(row.initial_price_local) || null;

    return {
        eurAmount: final,
        localAmount: localInitial !== null ? localInitial * (final / initial) : null,
    };
}
